<div class="admin-stats-dashboard flex h-full min-h-0 w-full flex-col overflow-hidden">
  <div class="shrink-0 p-5">
    <div class="flex justify-between items-center">
      <h4 class="m-0 text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">Business Stats</h4>
      <div class="flex items-center gap-3 pb-1 sm:pb-0">
        <label class="shout-toggle" for="business-stats-dashboard-show-shout-brands">
          <input
            id="business-stats-dashboard-show-shout-brands"
            type="checkbox"
            class="shout-toggle__input"
            [checked]="showShoutAgencyBrands()"
            (change)="onShowShoutAgencyBrandsChange($any($event.target).checked)"
          />
          <span class="shout-toggle__slider" aria-hidden="true"></span>
        </label>
        <p class="m-0 text-sm text-zinc-500">SHOUT brands</p>
      </div>
    </div>
    @if (rangeLabel) {
      <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
        Aggregate creator searches, profile visits, invites and hires across all businesses and agencies —
        {{ rangeLabel }}.
      </p>
    } @else {
      <p class="m-0 mt-2 max-w-4xl text-sm leading-relaxed text-zinc-600 dark:text-zinc-400">
        Aggregate creator searches, profile visits, invites and hires across all businesses and agencies.
      </p>
    }
  </div>

  <admin-stats-period-filter (periodChange)="onPeriodChange($event)"/>

  @if (loadingState === 'error' && error) {
    <div
      class="mx-5 mt-4 shrink-0 rounded-xl border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10"
    >
      <p class="m-0 text-sm text-rose-700 dark:text-rose-300">{{ error }}</p>
    </div>
  }

  <div class="flex min-h-0 flex-1 flex-col gap-5 overflow-auto px-5 pb-5 scrollbar-transparent">
    @if (loadingState === 'loading') {
      <atom-loading-inline title="Loading business stats" message="Fetching aggregates…" />
    } @else if (aggregate) {
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        @for (card of kpiCards; track card.key) {
          <section class="admin-stats-panel rounded-2xl p-5">
            <div class="flex items-center gap-2">
              <span class="size-2 rounded-full" [class]="kpiAccentDotClass(card.accent)" aria-hidden="true"></span>
              <p class="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
                {{ card.label }}
              </p>
            </div>
            <p
              class="m-0 mt-4 text-4xl font-bold leading-none tabular-nums text-zinc-900 dark:text-white xl:text-5xl"
            >
              {{ card.current.toLocaleString() }}
            </p>
            @let trend = getKpiTrend(card.current, card.previous);
            <p class="m-0 mt-4 text-sm">
              <span class="font-semibold" [class]="trend.trendClass">{{ trend.arrow }} {{ trend.percentLabel }}</span>
              <span class="text-zinc-500 dark:text-zinc-500"> {{ trend.comparisonLabel }}</span>
            </p>
          </section>
        }
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        @for (chart of metricCharts; track chart.key) {
          <section class="admin-stats-panel rounded-2xl p-5">
            <div class="mb-4 flex items-baseline justify-between gap-2">
              <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">{{ chart.label }}</h5>
              <p class="m-0 text-xs font-medium uppercase tracking-[0.16em] text-zinc-500">Per day</p>
            </div>
            @if (chart.hasData) {
              <app-echart-generic-chart
                class="block h-60 w-full"
                [chartOption]="chart.chartOption"
                [chartXaxisData]="chart.chartXaxisData"
                [chartVariant]="chart.chartVariant"
              />
            } @else {
              <div
                class="flex h-60 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10"
              >
                <p class="m-0 text-sm text-zinc-500">No data for this period yet.</p>
              </div>
            }
          </section>
        }
      </div>

      <div
        class="grid gap-4"
        [class]="showConversionFunnel ? 'xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]' : 'grid-cols-1'"
      >
        @if (showConversionFunnel) {
          <section class="admin-stats-panel rounded-2xl p-5">
            <div class="flex items-start justify-between gap-4">
              <div>
                <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">Conversion funnel</h5>
                <p class="m-0 mt-1 text-sm text-zinc-500">
                  Based on total searches (100% baseline) · {{ rangeLabel }}
                </p>
              </div>
              <div class="text-right">
                <p class="m-0 text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Searches</p>
                <p
                  class="m-0 mt-2 text-3xl font-bold leading-none tabular-nums text-zinc-900 dark:text-white xl:text-4xl"
                >
                  {{ aggregate.current.searches.toLocaleString() }}
                </p>
              </div>
            </div>

            <div class="mt-5 space-y-5">
              @for (bar of funnelBars; track bar.label; let i = $index) {
                <div>
                  <div class="mb-2 flex items-end justify-between gap-3">
                    <p class="m-0 text-sm font-medium text-zinc-900 dark:text-white">{{ bar.label }}</p>
                    <div class="text-right">
                      <p class="m-0 text-sm font-semibold tabular-nums text-zinc-900 dark:text-white">
                        {{ bar.value.toLocaleString() }}
                      </p>
                      <p class="m-0 text-xs tabular-nums text-zinc-500">{{ bar.percentOfBaseline.toFixed(1) }}%</p>
                    </div>
                  </div>
                  <div class="h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                    <div
                      class="h-full rounded-full transition-all duration-500"
                      [class]="funnelBarClass(i, funnelBars.length)"
                      [style.width.%]="bar.percentOfBaseline"
                    ></div>
                  </div>
                </div>
              }
            </div>
          </section>
        }

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          @for (board of leaderboards; track board.key) {
            <section class="admin-stats-panel rounded-2xl p-5">
              <h5 class="m-0 text-base font-semibold text-zinc-900 dark:text-white">{{ board.title }}</h5>
              <p class="m-0 mt-1 text-sm text-zinc-500">{{ board.subtitle }}</p>

              @if (board.rows.length > 0) {
                <ol class="mt-5 space-y-4">
                  @for (business of board.rows; track business.businessAccountId; let i = $index) {
                    <li>
                      <div class="flex items-center gap-3">
                        <p class="m-0 w-4 shrink-0 text-sm font-medium tabular-nums text-zinc-500">{{ i + 1 }}</p>
                        <div
                          class="relative size-10 shrink-0 overflow-hidden rounded-full bg-violet-100 ring-1 ring-violet-200 dark:bg-violet-500/20 dark:ring-violet-400/30"
                        >
                          <p
                            class="m-0 flex size-10 items-center justify-center text-xs font-semibold text-violet-700 dark:text-violet-200"
                          >
                            {{ companyInitials(business.companyName) }}
                          </p>
                        </div>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-center justify-between gap-3">
                            <p class="m-0 truncate text-sm font-semibold text-zinc-900 dark:text-white">
                              {{ business.companyName }}
                            </p>
                            <p class="m-0 shrink-0 text-sm font-semibold tabular-nums text-zinc-900 dark:text-white">
                              {{ business.metricValue.toLocaleString() }}
                            </p>
                          </div>
                          <div class="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-white/5">
                            <div
                              class="h-full rounded-full"
                              [class]="topActiveBarClass()"
                              [style.width.%]="business.barPercent"
                            ></div>
                          </div>
                          <p class="m-0 mt-1 text-xs text-zinc-500">
                            @if (business.agencyName) {
                              {{ business.agencyName }} · {{ business.secondaryValue.toLocaleString() }}
                              {{ board.secondarySuffix }}
                            } @else {
                              {{ business.secondaryValue.toLocaleString() }} {{ board.secondarySuffix }}
                            }
                          </p>
                        </div>
                      </div>
                    </li>
                  }
                </ol>
              } @else {
                <div
                  class="mt-6 flex min-h-48 items-center justify-center rounded-xl border border-dashed border-zinc-200 dark:border-white/10"
                >
                  <p class="m-0 text-sm text-zinc-500">No business activity for this period.</p>
                </div>
              }
            </section>
          }
        </div>
      </div>
    }
  </div>
</div>
